import React, { memo, useMemo, useEffect } from 'react';

import {
  useTable,
  useSortBy,
  useFilters,
  useExpanded,
  useRowSelect,
  usePagination,
  useGlobalFilter,
} from 'react-table';
import {
  Box,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TableFooter,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FaAngleUp, FaAngleDown } from 'react-icons/fa';
import { map, get, floor, isEmpty, isNumber, isFunction } from 'lodash';
import DefaultColumnFilter from './Filters/DefaultColumnFilter';
import { formatDateTime } from '@utils/formatIfDate';
import GlobalFilter from './Filters/GlobalFilter';
import styles from './DataTable.module.css';
import { EmptyContent } from '@components';
import TableLoading from './TableLoading';
import Pagination from './Pagination';
import { isDateFormat } from 'utils/formatIfDate';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles((theme) => ({
  checkBox: {
    accentColor: theme.palette.primary.main,
  },
}));
const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const classes = useStyles();
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <>
        <input
          className={classes.checkBox}
          type="checkbox"
          ref={resolvedRef}
          {...rest}
        />
      </>
    );
  },
);
const formatedCell = (cell) => {
  const cellValue = get(cell, 'value');
  if (isNumber(cellValue) && cellValue % 1 !== 0) {
    return floor(cellValue, 2);
  }
  if (isDateFormat(cellValue)) {
    return formatDateTime(cellValue);
  }
  return cell.render('Cell');
};

const DataTable = ({
  data,
  columns,
  isLoading,
  hasPageSize,
  hasPagination,
  reFetch,
  initialSortBy,
  initialFilters,
  handleDataSort,
  handlePageSize,
  isIndeterminateCheckbox,
  handlePageIndex,
  setSelectedFlatRows,
  handleDataFilter,
  initialSelectedRows,
  initialGlobalFilter,
  renderRowSubComponent,
  handleGlobalDataFilter,
  pageCount: controlledPageCount,
  ...rest
}) => {
  const { t } = useTranslation();
  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    [],
  );

  // Use the state and functions returned from useTable to build your UI
  const {
    page,
    nextPage,
    gotoPage,
    pageCount,
    prepareRow,
    canNextPage,
    setPageSize,
    previousPage,
    headerGroups,
    getTableProps,
    rows,
    canPreviousPage,
    setGlobalFilter,
    getTableBodyProps,
    preGlobalFilteredRows,
    state: { sortBy, filters, pageSize, pageIndex, globalFilter },
    selectedFlatRows,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState: {
        pageIndex: 0,
        pageSize: 15,
        sortBy: initialSortBy,
        filters: initialFilters,
        globalFilter: initialGlobalFilter,
        selectedRowIds: initialSelectedRows,
      },
      pageCount: controlledPageCount,
      manualSortBy: true,
      manualFilters: true,
      manualPagination: false,
      manualGlobalFilter: false,
      autoResetPage: false,
      reFetch,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      isIndeterminateCheckbox &&
        hooks.visibleColumns.push((columns) => [
          // Let's make a column for selection
          {
            id: 'selection',
            // The header can use the table's getToggleAllRowsSelectedProps method
            // to render a checkbox
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <div>
                <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
              </div>
            ),
            // The cell can use the individual row's getToggleRowSelectedProps method
            // to the render a checkbox
            Cell: ({ row }) => (
              <div>
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              </div>
            ),
          },
          ...columns,
        ]);
    },
  );

  useEffect(() => {
    if (selectedFlatRows.length !== 0) {
      setSelectedFlatRows(selectedFlatRows);
    } else {
      isIndeterminateCheckbox && setSelectedFlatRows([]);
    }
  }, [selectedFlatRows, setSelectedFlatRows, isIndeterminateCheckbox]);

  useEffect(() => {
    isFunction(handleDataSort) && handleDataSort(...sortBy);
  }, [sortBy, handleDataSort]);

  useEffect(() => {
    isFunction(handlePageSize) && handlePageSize(pageSize);
  }, [pageSize, handlePageSize]);

  useEffect(() => {
    isFunction(handlePageIndex) && handlePageIndex(pageIndex + 1);
  }, [pageIndex, handlePageIndex]);

  useEffect(() => {
    isFunction(handleDataFilter) && handleDataFilter(filters);
  }, [filters, handleDataFilter]);

  useEffect(() => {
    isFunction(handleGlobalDataFilter) && handleGlobalDataFilter(globalFilter);
  }, [globalFilter, handleGlobalDataFilter]);

  return (
    <>
      <Box>
        {isFunction(handleGlobalDataFilter) && (
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
        )}
        {isLoading && (
          <Box marginX="10px">
            <TableLoading repeat={6} height="62px" />
          </Box>
        )}
        <Box
          {...rest}
          maxWidth="100%"
          minHeight="300px"
          marginBottom={6}
          display={isLoading ? 'none' : 'block'}
          visibility={isLoading ? 'hidden' : 'visible'}>
          {isEmpty(rows) && !isFunction(handleDataFilter) ? (
            <EmptyContent height="100%" bgcolor="white" paddingY={10} />
          ) : (
            <Table {...getTableProps()}>
              <TableHead>
                {map(headerGroups, (headerGroup) => (
                  <TableRow {...headerGroup.getHeaderGroupProps()}>
                    {map(get(headerGroup, 'headers'), (column) => (
                      <TableCell
                        className={`${column.isSorted && styles.isSorted}`}
                        style={
                          column.setWidth
                            ? {
                                width: column.width,
                              }
                            : {}
                        }
                        {...column.getHeaderProps(
                          isFunction(handleDataSort) &&
                            column.getSortByToggleProps(),
                        )}>
                        {typeof column.render('Header') === 'string'
                          ? t(column.render('Header'))
                          : column.render('Header')}
                        <Box
                          right="0"
                          top="50%"
                          position="absolute"
                          style={{ transform: 'translateY(-50%)' }}>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <FaAngleDown />
                            ) : (
                              <FaAngleUp />
                            )
                          ) : (
                            ''
                          )}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHead>
              <TableBody {...getTableBodyProps()}>
                {map(page, (row, index) => {
                  prepareRow(row);
                  return (
                    <React.Fragment key={index}>
                      <TableRow hover {...row.getRowProps()}>
                        {map(get(row, 'cells'), (cell) => (
                          <TableCell {...cell.getCellProps()}>
                            {formatedCell(cell)}
                          </TableCell>
                        ))}
                      </TableRow>
                      {row.isExpanded ? renderRowSubComponent({ row }) : null}
                    </React.Fragment>
                  );
                })}
              </TableBody>
              {isFunction(handleDataFilter) && (
                <TableFooter>
                  {map(headerGroups, (headerGroup) => (
                    <TableRow {...headerGroup.getHeaderGroupProps()}>
                      {map(get(headerGroup, 'headers'), (column) => (
                        <TableCell {...column.getHeaderProps()}>
                          <Box marginTop={2}>
                            {column.canFilter ? column.render('Filter') : null}
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableFooter>
              )}
            </Table>
          )}
        </Box>
        {hasPagination && pageCount > 1 && (
          <Pagination
            pagesCount={pageCount}
            hasPageSize={hasPageSize}
            handlePrevPage={nextPage}
            pageIndex={pageIndex + 1}
            hasNextPage={!canNextPage}
            selectedPageSize={pageSize}
            handleNextPage={previousPage}
            hasPrevPage={!canPreviousPage}
            handleFirstPage={() => gotoPage(0)}
            handlePagSize={(e) => {
              setPageSize(Number(e.target.value));
            }}
            handleLastPage={() => gotoPage(pageCount - 1)}
          />
        )}
      </Box>
    </>
  );
};

DataTable.defaultProps = {
  rows: [],
  columns: [],
  pageCount: 0,
  isLoading: false,
  initialSortBy: [],
  hasPageSize: false,
  initialFilters: [],
  hasPagination: false,
  initialGlobalFilter: '',
  initialSelectedRows: {},
};

DataTable.propTypes = {
  rows: PropTypes.array,
  isLoading: PropTypes.bool,
  columns: PropTypes.array,
  hasPageSize: PropTypes.bool,
  pageCount: PropTypes.number,
  hasPagination: PropTypes.bool,
  initialSortBy: PropTypes.array,
  handleDataSort: PropTypes.func,
  handlePageSize: PropTypes.func,
  initialFilters: PropTypes.array,
  handlePageIndex: PropTypes.func,
  handleDataFilter: PropTypes.func,
  initialGlobalFilter: PropTypes.string,
  initialSelectedRows: PropTypes.object,
};

export default memo(DataTable);
